/**
 * Deux lignes bancaires qui décrivent le même mouvement.
 *
 * ## Le défaut que ce module ferme
 *
 * Le 27 septembre 2026, dix-sept relevés CODA ont été importés sur une période
 * que l'export CSV du 18 septembre couvrait déjà jusqu'au 17/09. Le CODA ne
 * reconnaissait un mouvement qu'à sa propre référence bancaire, et seulement
 * parmi les lignes CODA : **48 mouvements écrits deux fois**, 44 entrées
 * (4 901,80 €) et 4 sorties (1 936,83 €). Dans la file, un virement de
 * 173,00 € qui avait déjà soldé sa facture se présentait comme une
 * « communication structurée inconnue de nos factures ».
 *
 * Les deux formats n'ont aucun identifiant commun. Ce qui les rapproche, c'est
 * la **forme** — sens, date valeur, montant, compte — puis la
 * **communication**, qui dit si deux lignes de même forme parlent de la même
 * chose.
 *
 * ## Les trois cas, et ce que chacun devient
 *
 * | communications | ce que c'est                                        | ce qu'on en fait |
 * |----------------|-----------------------------------------------------|------------------|
 * | `identiques`   | le même mouvement, lu deux fois                     | relié, jamais réécrit |
 * | `une_absente`  | l'une des deux ne dit rien : rien ne les distingue  | relié, comme l'import CSV le fait depuis le 18/09 |
 * | `differentes`  | même forme, deux textes : **doublon probable**      | écrit, signalé — un humain tranche |
 *
 * Deux virements réellement distincts peuvent avoir la même forme : un parent
 * qui règle deux factures du même montant le même jour. C'est pour eux que la
 * communication passe AVANT le rang : sans cela, les deux se croisent — le
 * 18 septembre 2026, l'appariement au seul rang en avait croisé deux paires.
 *
 * ## Une règle, deux lecteurs qui disent la même chose
 *
 * `cleDeCommunication`, `comptesConcordent` et `communicationsConcordent`
 * existent aussi en SQL (`public.cle_de_communication`,
 * `public.comptes_concordent`, `public.communications_concordent`, migrations
 * `20260927144800` et `20260927160609`), parce que le contrôle
 * `v_mouvements_bancaires_en_double` et la base elle-même en ont besoin. Les
 * deux versions sont écrites pour rendre exactement la même chose, et les
 * tests les éprouvent sur les mêmes cas réels.
 */

/** Le sens d'un mouvement, tel que la base le range : `payments` ou `refunds`. */
export type Sens = 'entree' | 'sortie';

/** Ce que deux communications disent l'une de l'autre. */
export type Communications = 'identiques' | 'une_absente' | 'differentes';

/** Ce qu'il faut connaître d'une ligne bancaire pour l'apparier. */
export interface FormeBancaire {
  valueDate: string;
  amountCents: number;
  counterpartyIban?: string | null;
  structuredDigits?: string | null;
  communicationRaw?: string | null;
}

/** Une ligne de l'export CSV qu'un relevé pourrait décrire. */
export interface LigneCsv extends FormeBancaire {
  sens: Sens;
  id: string;
  allocatedCents: number;
}

export interface Appariement<M> {
  mouvement: M;
  ligne: LigneCsv;
  communications: Communications;
}

export interface ResultatAppariement<M> {
  /** `identiques` et `une_absente` : le même mouvement, à relier sans le réécrire. */
  reunis: Appariement<M>[];
  /** `differentes` : écrits quand même, et signalés. */
  probables: Appariement<M>[];
  /** Tout ce qui n'a pas de jumeau sûr — probables compris : ils s'écrivent. */
  nouveaux: M[];
}

/*
  Les libellés que la banque met à la place d'une communication absente.
  Mesurés en production le 27/09/2026 sur les lignes CODA : 54 « Virement
  instantané en euros », 35 « Virement en euros (SEPA) ». Ils ne disent rien du
  virement ; les compter comme une communication ferait conclure « différentes »
  face à un export qui, lui, laisse le champ vide.
*/
const LIBELLES_GENERIQUES = new Set(['VIREMENTENEUROSSEPA', 'VIREMENTINSTANTANENEUROS']);

/**
 * La clé qui dit si deux lignes portent la même communication.
 *
 * - structurée : ses douze chiffres (`S…`) — le CODA les donne nus, le CSV
 *   entre astérisques, la lecture les ramène aux mêmes chiffres ;
 * - libre : lettres et chiffres ASCII, en majuscules (`T…`). Les accents et la
 *   ponctuation tombent AVANT la mise en majuscules, pour que PostgreSQL et
 *   JavaScript rendent la même clé — `ß` ne devient pas `SS` d'un côté
 *   seulement ;
 * - rien, ou un libellé générique de la banque : `null`. « On ne sait pas »
 *   n'est pas « c'est différent ».
 */
export function cleDeCommunication(
  structuredDigits: string | null | undefined,
  communicationRaw: string | null | undefined,
): string | null {
  const chiffres = (structuredDigits ?? '').replace(/[^0-9]/g, '');
  if (chiffres) return `S${chiffres}`;
  const texte = (communicationRaw ?? '').replace(/[^A-Za-z0-9]/g, '').toUpperCase();
  if (!texte || LIBELLES_GENERIQUES.has(texte)) return null;
  return `T${texte}`;
}

/** Un compte sans aucun blanc, en majuscules. Même règle que `public.normaliser_compte`. */
export function normaliserCompte(iban: string | null | undefined): string {
  return (iban ?? '').replace(/\s/g, '').toUpperCase();
}

/**
 * Le compte lu par le relevé et celui de l'export désignent-ils le même payeur ?
 *
 * **L'export doit en porter un.** Une ligne de l'export sans compte n'est pas
 * un virement — dépôt d'espèces, carte, frais — et le relevé n'importe QUE
 * des virements : elle n'est le jumeau de personne. Jusqu'au 27/09/2026, un
 * compte vide était « compatible » avec tous, et un virement du relevé
 * pouvait être relié à un dépôt d'espèces du même jour et du même montant, sur
 * la seule coïncidence date + montant — puis ne jamais être écrit.
 *
 * Le relevé, lui, peut ne pas en connaître (anciennes lignes) : c'est alors à
 * la communication de trancher, pas à cette règle. Sinon, égalité stricte.
 * Même règle que `public.comptes_concordent`.
 */
export function comptesConcordent(releve: string | null | undefined, exportCsv: string | null | undefined): boolean {
  const e = normaliserCompte(exportCsv);
  if (!e) return false;
  const r = normaliserCompte(releve);
  return !r || r === e;
}

/**
 * Deux clés de communication disent-elles la même chose ?
 *
 * Égales ; ou, entre deux textes, la plus courte (10 caractères au moins)
 * contenue dans l'autre — le champ communication du relevé s'arrête à 53
 * caractères, l'export porte le texte entier ; ou les douze chiffres d'une
 * communication structurée lus dans le texte de l'autre. Même règle que
 * `public.communications_concordent`.
 */
export function communicationsConcordent(a: string | null, b: string | null): boolean {
  if (!a || !b) return false;
  if (a === b) return true;
  const [ta, tb] = [a.slice(1), b.slice(1)];
  if (a[0] === 'T' && b[0] === 'T') {
    const [court, long] = ta.length <= tb.length ? [ta, tb] : [tb, ta];
    return court.length >= 10 && long.includes(court);
  }
  if (a[0] === 'S' && b[0] === 'T') return tb.includes(ta);
  if (a[0] === 'T' && b[0] === 'S') return ta.includes(tb);
  return false;
}

function memeFormeSansCompte(a: FormeBancaire, b: FormeBancaire): boolean {
  return a.valueDate === b.valueDate && a.amountCents === b.amountCents;
}

/**
 * Apparie les mouvements d'un relevé avec les lignes de l'export CSV qui
 * décrivent peut-être les mêmes.
 *
 * Trois passes, de la plus sûre à la moins sûre, chaque ligne CSV ne servant
 * qu'une fois — les mêmes que le contrôle `v_mouvements_bancaires_en_double` :
 *
 * 1. même communication, même compte ;
 * 1 bis. même communication, compte inconnu du SEUL côté relevé ;
 * 2. le reste, même compte, au rang — les communications absentes d'abord :
 *    `identiques` si elles concordent (texte tronqué), `une_absente` si l'une
 *    ne dit rien, `differentes` sinon.
 *
 * Une ligne de l'export sans compte n'entre dans aucune passe.
 *
 * Ne modifie rien : rend ce qu'il a trouvé, et l'appelant décide.
 */
export function apparierAvecLExportCsv<M extends FormeBancaire>(
  mouvements: M[],
  lignes: LigneCsv[],
): ResultatAppariement<M> {
  const candidates = lignes.filter((l) => normaliserCompte(l.counterpartyIban) !== '');
  const cles = mouvements.map((m) => cleDeCommunication(m.structuredDigits, m.communicationRaw));
  const comptes = mouvements.map((m) => normaliserCompte(m.counterpartyIban));
  const clesCsv = new Map(candidates.map((l) => [l.id, cleDeCommunication(l.structuredDigits, l.communicationRaw)]));
  const prises = new Set<string>();
  const trouve = new Map<number, { ligne: LigneCsv; communications: Communications }>();

  const prendre = (i: number, l: LigneCsv, communications: Communications) => {
    prises.add(l.id);
    trouve.set(i, { ligne: l, communications });
  };

  // Passe 1 : la même communication, le même compte.
  mouvements.forEach((m, i) => {
    const cle = cles[i];
    if (cle === null || comptes[i] === '') return;
    const l = candidates.find((c) => !prises.has(c.id) && memeFormeSansCompte(m, c)
      && normaliserCompte(c.counterpartyIban) === comptes[i] && clesCsv.get(c.id) === cle);
    if (l) prendre(i, l, 'identiques');
  });

  // Passe 1 bis : la même communication, un compte que seul le relevé ignore.
  mouvements.forEach((m, i) => {
    const cle = cles[i];
    if (trouve.has(i) || cle === null || comptes[i] !== '') return;
    const l = candidates.find((c) => !prises.has(c.id) && memeFormeSansCompte(m, c) && clesCsv.get(c.id) === cle);
    if (l) prendre(i, l, 'identiques');
  });

  // Passe 2 : le reste, au rang, à compte égal — les communications absentes d'abord.
  const ordre = mouvements
    .map((_m, i) => i)
    .sort((a, b) => Number(cles[a] !== null) - Number(cles[b] !== null) || a - b);
  const lignesOrdonnees = candidates
    .map((l, rang) => ({ l, rang }))
    .sort((a, b) => Number(clesCsv.get(a.l.id) !== null) - Number(clesCsv.get(b.l.id) !== null) || a.rang - b.rang)
    .map((x) => x.l);

  for (const i of ordre) {
    if (trouve.has(i) || comptes[i] === '') continue;
    const m = mouvements[i];
    const l = lignesOrdonnees.find((c) => !prises.has(c.id) && memeFormeSansCompte(m, c)
      && normaliserCompte(c.counterpartyIban) === comptes[i]);
    if (!l) continue;
    const a = cles[i];
    const b = clesCsv.get(l.id) ?? null;
    prendre(i, l, a === null || b === null ? 'une_absente'
      : communicationsConcordent(a, b) ? 'identiques' : 'differentes');
  }

  const reunis: Appariement<M>[] = [];
  const probables: Appariement<M>[] = [];
  const nouveaux: M[] = [];
  mouvements.forEach((m, i) => {
    const t = trouve.get(i);
    if (!t) { nouveaux.push(m); return; }
    const a = { mouvement: m, ligne: t.ligne, communications: t.communications };
    if (t.communications === 'differentes') { probables.push(a); nouveaux.push(m); }
    else reunis.push(a);
  });

  return { reunis, probables, nouveaux };
}

/** Une ligne bancaire de la base, telle que la file la connaît, pour lui chercher un jumeau. */
export interface LigneBancaire extends FormeBancaire {
  id: string;
  source: string;
  /** Quand la ligne a été écrite : une ligne de l'export écrite APRÈS celle du relevé n'est pas son doublon. */
  createdAt?: string | null;
}

export interface Jumeau<L> {
  autre: L;
  communications: Communications;
}

/**
 * Le jumeau d'une ligne bancaire dans l'autre source, s'il en a un.
 *
 * Sert à la file et au compte de la famille : une ligne en attente qui a, de
 * l'autre côté (relevé CODA ↔ export CSV), une ligne de même forme est très
 * probablement le même virement lu deux fois. C'est le même appariement que
 * celui de l'import — une notion, un lecteur — et les mêmes exclusions que le
 * contrôle :
 *
 * - une ligne de l'export écrite APRÈS celle du relevé n'est pas son doublon :
 *   l'import du CSV compte ce que la base porte et n'écrit que ce qui manque ;
 * - une paire qu'un humain a déclarée distincte (`distinctes`, clés
 *   `idReleve|idExport`) ne l'est plus.
 */
export function jumeauDe<A extends LigneBancaire>(
  ligne: LigneBancaire,
  autres: A[],
  distinctes: ReadonlySet<string> = new Set(),
): Jumeau<A> | null {
  const avant = (csv?: string | null, releve?: string | null) => !csv || !releve || csv <= releve;

  if (ligne.source === 'bank') {
    const exports = autres.filter((a) => a.id !== ligne.id && a.source === 'bank_csv'
      && avant(a.createdAt, ligne.createdAt) && !distinctes.has(`${ligne.id}|${a.id}`));
    const { reunis, probables } = apparierAvecLExportCsv([ligne], exports.map((a) => ({
      ...a, sens: 'entree' as const, allocatedCents: 0,
    })));
    const trouve = reunis[0] ?? probables[0];
    const autre = trouve ? exports.find((a) => a.id === trouve.ligne.id) : undefined;
    return trouve && autre ? { autre, communications: trouve.communications } : null;
  }

  if (ligne.source === 'bank_csv') {
    const releves = autres.filter((a) => a.id !== ligne.id && a.source === 'bank'
      && avant(ligne.createdAt, a.createdAt) && !distinctes.has(`${a.id}|${ligne.id}`));
    const { reunis, probables } = apparierAvecLExportCsv(releves, [{
      ...ligne, sens: 'entree' as const, allocatedCents: 0,
    }]);
    const trouve = reunis[0] ?? probables[0];
    return trouve ? { autre: trouve.mouvement, communications: trouve.communications } : null;
  }

  return null;
}
